import { formatPrice } from "../../../../../../common/helpers/formatPrice";
import { CurrencyInfo } from "../../../../../../common/helpers/currency";
import type {
  RawShippingRatePackage,
  RawShippingRate,
} from "../../../../../../types";
import type { TransformedShippingRate } from "../../../types/cartApiTypes";

/**
 * A pure helper function to transform and flatten shipping rate packages.
 * @param shippingRatePackages The raw shipping_rates array from the WC Store API cart response.
 * @param currencyInfo An object containing currency formatting rules.
 * @returns A flattened array of transformed shipping rates.
 */
export const transformShippingRates = (
  shippingRatePackages: RawShippingRatePackage[],
  currencyInfo: CurrencyInfo,
): TransformedShippingRate[] => {
  if (!Array.isArray(shippingRatePackages)) {
    return [];
  }

  return shippingRatePackages.flatMap((pkg) =>
    pkg.shipping_rates.map((rate: RawShippingRate): TransformedShippingRate => {
      // --- MATHEMATICAL TAX-INCLUSIVE SUM ---
      // Sum Net Price + Tax to output the complete tax-inclusive value
      const rawPriceInt = parseInt(rate.price || "0", 10);
      const rawTaxesInt = parseInt(rate.taxes || "0", 10);
      const grossPriceInt = rawPriceInt + rawTaxesInt;

      return {
        rateId: rate.rate_id,
        name: rate.name,
        description: rate.description,
        deliveryTime: rate.delivery_time,
        // Price is now the Gross (Tax Inclusive) value
        price: formatPrice(String(grossPriceInt), currencyInfo),
        // We keep the net price explicitly available just in case it's needed later
        netPrice: formatPrice(rate.price, currencyInfo),
        taxes: formatPrice(rate.taxes, currencyInfo),
        methodId: rate.method_id,
        selected: rate.selected,
      };
    }),
  );
};
